import React, { useState } from "react";
import { motion } from "framer-motion";
import styled from "styled-components";
import { isMobile } from "react-device-detect";

const colors = {
  top: ["#dba008", "#df2d8e"],
  right: ["magenta", "cyan"],
  bot: ["cyan", "green"],
  left: ["#7b4397 0%, #7b4397 25%", "#dc2430 75%, #dc2430 100%"],
};

const ProjectContentVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.5,
    },
  },
};

const Container = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: row;

  @media (max-width: 800px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const VertContainer = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-left: 30px;

  @media (max-width: 800px) {
    padding-left: 0;
  }
`;

const MenuContainer = styled(motion.div)`
  width: 33%;
  display: flex;
  flex-direction: row;

  @media (max-width: 800px) {
    width: 100%;
    flex-direction: column;
  }
`;

const AbsoluteWrapper = styled(motion.div)`
  position: ${(props) => (props.enabled ? "absolute" : "relative")};
`;

const ContentContainer = styled(motion.div)`
  width: 85%;
  min-height: 300px;
  display: flex;

  @media (max-width: 800px) {
    width: 100%;
  }

  ${(props) => {
    if (!props.smallDevice)
      return `
            &:before {
                content: "";
                position: absolute;
                height: 260px;
                width: 4px;
                transform: translateX(3px);
                background-color: white;
                opacity: 0.2;
                border-radius: 3px;
            }
        `;
  }}
`;

const MenuItemContainer = styled(motion.div)`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 10px;
  cursor: pointer;

  @media (max-width: 800px) {
    margin-top: 6px;
  }
`;

const MenuText = styled(motion.text)`
  font-family: Outfit;
  font-weight: 400;
  font-size: 24px;
  color: white;
  opacity: ${(props) => (props.selected ? 0.9 : 0.6)};

  &:hover {
    opacity: ${(props) => (props.selected ? 0.9 : 0.7)};
  }

  @media (max-width: 800px) {
    font-size: 18px;
  }
`;

const Text = styled(motion.text)`
  font-family: Outfit;
  font-size: 22px;
  font-weight: 300;
  color: #fcfafb;
  opacity: ${(props) => props.opacity ?? 1};

  @media (max-width: 800px) {
    font-size: 16px;
  }
`;

const Heading = styled(motion.text)`
  font-family: Inter;
  font-weight: bold;
  font-size: 32px;
  color: white;
  opacity: 1;

  @media (max-width: 800px) {
    font-size: 22px;
  }
`;

const Spacer = styled(motion.div)`
  height: ${(props) => props.height ?? 20}px;
`;

const List = styled(motion.ul)`
  margin: 0;
  padding-left: 22px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ListItem = styled(motion.li)`
  font-family: Outfit;
  font-size: 22px;
  font-weight: 300;
  color: #fcfafb;
  opacity: ${(props) => props.opacity ?? 1};

  @media (max-width: 800px) {
    font-size: 16px;
  }
`;
const SpanRainbow = styled(motion.span)`
  font-family: inherit;
  font-size: inherit;
  font-weight: ${(props) => {
    if (props.bold) return "500;";
    else return "inherit;";
  }};

  ${(props) => {
    let gradientColors = props.colors ?? colors["top"];
    let start = props.start ?? "bottom right";
    return `background: -webkit-linear-gradient(${start}, ${gradientColors[0]}, ${gradientColors[1]});`;
  }}
  background-clip: text;
  text-fill-color: transparent;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SpanPink = styled(motion.span)`
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  color: #fd71cd;
  transition-duration: 0.1s;
  cursor: pointer;

  &:hover {
    color: #e33daa;
  }
`;

const SubTabRow = styled(motion.div)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
`;

const SubTabPill = styled(motion.div)`
  font-family: Outfit;
  font-size: 14px;
  font-weight: 400;
  color: white;
  cursor: pointer;
  padding: 6px 16px;
  border-radius: 100px;
  opacity: ${(props) => (props.selected ? 1 : 0.55)};
  border: 1px solid
    rgba(255, 255, 255, ${(props) => (props.selected ? 0.35 : 0.15)});
  background: rgba(255, 255, 255, ${(props) => (props.selected ? 0.1 : 0.03)});
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
    border-color: rgba(255, 255, 255, 0.3);
  }

  @media (max-width: 800px) {
    font-size: 12px;
    padding: 5px 12px;
  }
`;

const RobloxEngineerSections = [
  {
    name: "Avatar Safety",
    content: (
      <>
        <Text>
          Part of a small team of three that owns Roblox's avatar moderation
          end-to-end — modeling, infra, and reliability. Took it from reactive
          to proactive for the first time, scaling scan throughput from{" "}
          <SpanRainbow bold>20 qps</SpanRainbow> to{" "}
          <SpanRainbow bold>50,000 QPS</SpanRainbow> in pure Python while
          improving quality. This <SpanRainbow bold>2,500×</SpanRainbow>{" "}
          improvement was a major advancement, allowing us to watch the whole
          platform continuously instead of just reacting to reports. (
          <SpanPink
            onClick={() =>
              window.open(
                "https://about.roblox.com/newsroom/2026/01/safety-snapshot-avatar-moderation",
              )
            }
          >
            Safety Snapshot writeup
          </SpanPink>
          )
        </Text>
        <Spacer height={15} />
        <Text>Some highlights:</Text>
        <Spacer height={10} />
        <List>
          <ListItem>
            Designed a <SpanRainbow bold>Redis</SpanRainbow>-backed tier-0 user
            reputation filter that skips the long tail of avatars made up
            mostly of already-trusted assets, cutting actual scan volume by 5x
            to <SpanRainbow bold>10K QPS</SpanRainbow>.
          </ListItem>
          <ListItem>
            Aggressively optimized our entire ML inference stack for the{" "}
            <SpanRainbow bold>Rtx6k Blackwell</SpanRainbow> architecture using{" "}
            <SpanRainbow bold>TensorRT</SpanRainbow> + mixed FP8 quantization
            to boost throughput/chip by <SpanRainbow bold>10x</SpanRainbow>.
          </ListItem>
          <ListItem>
            Achieved a <SpanRainbow bold>20×+</SpanRainbow> jump in detection
            recall by expanding the violation taxonomy through feature
            engineering and label distillation. Relabeled years of mislabeled
            data by clustering it with{" "}
            <SpanRainbow bold>CLIP / SigLip</SpanRainbow>, and doing
            large-scale <SpanRainbow bold>LLM</SpanRainbow> label synthesis.
          </ListItem>
        </List>
      </>
    ),
  },
  {
    name: "Game Safety",
    content: (
      <>
        <Text>
          Built our team's centralized{" "}
          <SpanRainbow bold>ML infrastructure</SpanRainbow> and model-lifecycle
          system — push-button deploys backed by shadow traffic, automated
          backups, and one-click rollbacks — turning what used to be a manual
          1-hour release process into a non-event.
        </Text>
        <Spacer height={15} />
        <Text>
          Also implemented several{" "}
          <SpanRainbow bold>fingerprinting systems</SpanRainbow> that
          meaningfully increased recall on groups of DMAs (dedicated malicious
          actors) operating across games.
        </Text>
      </>
    ),
  },
  {
    name: "Tooling",
    content: (
      <>
        <Text>
          Built the investigation tooling the team uses to debug — a searchable,
          data-rich view into any asset's scan history and enforcement decisions
          (backed by <SpanRainbow bold>ElasticSearch</SpanRainbow> and{" "}
          <SpanRainbow bold>DynamoDB</SpanRainbow>) — which sped up incident
          response and became the template other safety teams build on now.
        </Text>
        <Spacer height={15} />
        <Text>
          Also helped build our labeling and sample-curation tools, which
          automated a lot of dataset generation that used to be manual work.
        </Text>
      </>
    ),
  },
];

const RobloxEngineer = () => {
  const [selectedSection, setSelectedSection] = useState(0);

  return (
    <VertContainer
      key={"RobloxEngineer"}
      variants={ProjectContentVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <Heading>Software Engineer – Content Safety</Heading>
      <Text opacity={0.6}>August 2024 – Present</Text>
      <Spacer height={20} />
      <Text opacity={0.7}>
        A few of the things I've gotten to sink my teeth into:
      </Text>
      <Spacer height={15} />
      <SubTabRow>
        {RobloxEngineerSections.map((section, index) => (
          <SubTabPill
            key={section.name}
            selected={selectedSection === index}
            onClick={() => setSelectedSection(index)}
          >
            {section.name}
          </SubTabPill>
        ))}
      </SubTabRow>
      <Spacer height={20} />
      <motion.div
        key={selectedSection}
        variants={ProjectContentVariants}
        initial="hidden"
        animate="visible"
      >
        {RobloxEngineerSections[selectedSection].content}
      </motion.div>
    </VertContainer>
  );
};

const RobloxIntern = () => {
  return (
    <VertContainer
      key={"RobloxIntern"}
      variants={ProjectContentVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <Heading>Software Engineering Intern</Heading>
      <Text opacity={0.6}>June - September 2023</Text>
      <Spacer height={20} />
      <Text>
        Designed a cross-platform{" "}
        <SpanRainbow bold>UI layout engine</SpanRainbow> for an unreleased{" "}
        <SpanPink onClick={() => window.open("https://roblox.com")}>
          Roblox
        </SpanPink>{" "}
        product, prioritizing modularity and privacy at the framework level.
      </Text>
      <Spacer height={20} />
      <Text>
        Built a queued notification system that raised error-handling coverage
        from <SpanRainbow bold>5% to 95%</SpanRainbow>, hardening state
        synchronization across distributed client-server instances.
      </Text>
    </VertContainer>
  );
};

const DittoEngineer = () => {
  return (
    <VertContainer
      key={"DittoEngineer"}
      variants={ProjectContentVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <Heading>Software Engineer (Part-time)</Heading>
      <Text opacity={0.6}>October 2022 - June 2023</Text>
      <Spacer height={20} />
      <Text>
        Presented growth and social-graph analysis in{" "}
        <SpanRainbow bold>Series A</SpanRainbow> investor pitches for{" "}
        <SpanPink onClick={() => window.open("https://ditto-app.com")}>
          Ditto Labs
        </SpanPink>
        , helping close a successful raise.
      </Text>
      <Spacer height={20} />
      <Text>
        Helped steer product direction with user research and usage data —
        including verified-content and moderation features that drove a{" "}
        <SpanRainbow bold>16×</SpanRainbow> increase in feature usage.
      </Text>
    </VertContainer>
  );
};

const ReelayEngineer = () => {
  return (
    <VertContainer
      key={"ReelayEngineer"}
      variants={ProjectContentVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <Heading>Founding Software Engineer</Heading>
      <Text opacity={0.6}>January 2022 - October 2022</Text>
      <Spacer height={20} />
      <Text>
        Owned the end-to-end user experience as a founding engineer for{" "}
        <SpanPink onClick={() => window.open("https://reelay.app")}>
          Reelay
        </SpanPink>
        , influencing product direction and roadmap through user research and
        product intuition.
      </Text>
      <Spacer height={20} />
      <Text>
        Built the app's UI layer from scratch — including an{" "}
        <SpanRainbow bold>animations module</SpanRainbow> and a{" "}
        <SpanRainbow bold>Next.js</SpanRainbow> web VM — plus a{" "}
        <SpanRainbow bold>recommendation system</SpanRainbow> to sharpen content
        discovery.
      </Text>
    </VertContainer>
  );
};

const Education = () => {
  return (
    <VertContainer
      key={"Education"}
      variants={ProjectContentVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <Heading>University of California, Santa Barbara</Heading>
      <Text opacity={0.6}>August 2021 - June 2024</Text>
      <Spacer height={20} />
      <Text>
        Bachelor of Science in <SpanRainbow bold>Computer Science</SpanRainbow>;
        GPA: <SpanRainbow bold>4.0</SpanRainbow> (Focus in Graph Theory & ML).
      </Text>
    </VertContainer>
  );
};

export default function ExperienceMenu({ colors }) {
  const [selectedItem, setSelectedItem] = useState(0);
  const [renderAnimation, setRenderAnimation] = useState(true);

  const smallDevice = isMobile || window.innerWidth < 800;

  // [ Name, Component ]
  const Experiences = [
    ["Roblox - Engineer", RobloxEngineer],
    ["Roblox - Intern", RobloxIntern],
    ["Ditto - Engineer", DittoEngineer],
    ["Reelay - Engineer", ReelayEngineer],
    ["Education", Education],
  ];

  const GradientBar = styled(motion.div)`
    height: 30px;
    width: 4px;
    ${(props) =>
      props.selected
        ? `background: linear-gradient(to bottom, ${colors[0]}, ${colors[1]})`
        : ""};
    border-radius: 2px;
    margin-right: ${(props) => (props.selected ? `20px` : "0px")};
    ${(props) =>
      props.selected ? "animation: animateExperience 0.2s ease-out;" : ""}

    @keyframes animateExperience {
      0% {
        margin-right: 0px;
        background: none;
      }
      20% {
        margin-right: 4px;
        background: linear-gradient(to bottom, ${colors[0]}, ${colors[1]});
      }
      to {
        margin-right: 20px;
        background: linear-gradient(to bottom, ${colors[0]}, ${colors[1]});
      }
    }
  `;

  const MenuTabItem = ({ id, text }) => {
    const selected = selectedItem === id;
    const handleClick = () => {
      setRenderAnimation(false);
      setSelectedItem(id);
      setRenderAnimation(true);
    };
    return (
      <MenuItemContainer onClick={handleClick}>
        {selected && <GradientBar selected={selected} />}
        <MenuText selected={selected}>{text}</MenuText>
      </MenuItemContainer>
    );
  };

  return (
    <Container>
      <MenuContainer>
        <AbsoluteWrapper>
          {Experiences.map((item, index) => (
            <MenuTabItem key={index} id={index} text={item[0]} />
          ))}
        </AbsoluteWrapper>
      </MenuContainer>
      <ContentContainer smallDevice={smallDevice}>
        {renderAnimation &&
          (() => {
            const SelectedExperience = Experiences[selectedItem][1];
            return <SelectedExperience />;
          })()}
      </ContentContainer>
    </Container>
  );
}
